import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { FileText, History, PackageCheck } from "lucide-react";

const tone = (e) => e.cancelled || e.status === "CANCELLED" ? "bg-slate-100 text-slate-500 border-slate-200"
  : e.draft ? "bg-amber-50 text-amber-900 border-amber-200" : "bg-emerald-50 text-emerald-800 border-emerald-200";

export function useSerialHistory(serial) {
  const [h, setH] = useState(null);
  useEffect(() => {
    if (!serial) return;
    api.get(`/devices/${encodeURIComponent(serial)}/usage-history`).then(({ data }) => setH(data)).catch(() => setH({ events: [] }));
  }, [serial]);
  return h;
}

export default function SerialUsageHistory({ history }) {
  if (!history) return <div className="os-skeleton h-24" />;
  const ev = history.events || [];
  return (
    <section className="os-card p-4" data-testid="serial-usage-history">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <History className="h-4 w-4 text-[color:var(--st-text-muted)]" />
        <h2 className="text-[13px] font-semibold">Where this serial was used</h2>
        <span className="text-[11px] text-[color:var(--st-text-muted)]" data-testid="serial-usage-counts">{history.orders || 0} order(s) · {history.dcs || 0} DC(s)</span>
        {!history.in_inventory && ev.length > 0 && <span className="ml-auto rounded border border-sky-200 bg-sky-50 px-1.5 text-[10px] font-semibold text-sky-800" data-testid="serial-usage-reference">Not in inventory</span>}
      </div>
      {!ev.length ? <div className="text-[12px] text-[color:var(--st-text-muted)]" data-testid="serial-usage-empty">This serial has not been used on any order or Delivery Challan yet.</div> : (
        <ol className="relative border-l border-[color:var(--st-border)] ml-1.5 space-y-3" data-testid="serial-usage-list">
          {ev.map((e, i) => (
            <li key={i} className="pl-4 relative" data-testid={`serial-usage-${e.kind.toLowerCase()}-${i}`}>
              <span className="absolute -left-[5px] top-1.5 h-2.5 w-2.5 rounded-full bg-white border-2 border-[color:var(--st-accent)]" />
              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12px]">
                <span className="font-mono text-[11px] text-[color:var(--st-text-muted)]">{e.date || "—"}</span>
                {e.kind === "DC" ? <FileText className="h-3.5 w-3.5" /> : <PackageCheck className="h-3.5 w-3.5" />}
                <span className="font-semibold">{e.kind === "DC" ? e.dc_code : "Allocated on order"}</span>
                {e.order_id && <Link to={`/commercial/one-orders?open=${e.order_id}`} className="font-mono text-[11px] text-[color:var(--st-accent)] hover:underline" data-testid={`serial-usage-order-link-${i}`}>{e.order_code}</Link>}
                <span className={`rounded border px-1.5 text-[10px] font-semibold ${tone(e)}`}>{String(e.status || "").replace(/_/g, " ")}{e.draft ? " · draft" : ""}</span>
                {e.added_on_dc && <span className="rounded border border-sky-200 bg-sky-50 px-1 text-[9px] font-semibold uppercase text-sky-800">Added on DC</span>}
                {e.reference_only && <span className="rounded border border-slate-200 bg-slate-50 px-1 text-[9px] font-semibold uppercase text-slate-600">Reference</span>}
              </div>
              <div className="text-[12px] text-[color:var(--st-text-muted)] break-words">{[e.item, e.recipient && `→ ${e.recipient}`, e.customer_name, e.awb && `AWB ${e.awb}`].filter(Boolean).join(" · ")}</div>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
